class CartDiscountCode extends HTMLElement {
  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    if (this.discountHandlersInitialized) return;
    this.discountHandlersInitialized = true;

    this._bindEvents();
    this._initAccordionA11yState();
    this._initDiscountState();
    this._observeCartEmpty();
    this._bindPageShow();
  }

  disconnectedCallback() {
    if (this._onHostClick) {
      this.removeEventListener('click', this._onHostClick);
      this._onHostClick = null;
    }

    if (this._detailsToggleHandler && this._discountDetails) {
      this._discountDetails.removeEventListener('toggle', this._detailsToggleHandler);
      this._detailsToggleHandler = null;
    }

    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow);
      this._onPageShow = null;
    }
  }

  // Catch the back-navigation-from-checkout case: browsers often restore the page from
  // bfcache (no reload), so a discount removed at checkout leaves stale UI/totals here
  // unless we explicitly resync against the server.
  _bindPageShow() {
    this._onPageShow = (event) => {
      if (!event.persisted) return;
      this._initDiscountState();
      this._fetchUpdatedCart();
    };
    window.addEventListener('pageshow', this._onPageShow);
  }

  // ─── Initialization ───────────────────────────────────────────────────────

  _bindEvents() {
    // Use delegated click handling so buttons keep working after cart section re-renders.
    this._onHostClick = (e) => {
      const applyBtn = e.target.closest('#apply-discount');
      if (applyBtn && this.contains(applyBtn)) {
        e.preventDefault();
        const code = this.querySelector('#discount-code')?.value.trim();
        if (code) this.applyDiscount(code).catch(console.error);
        return;
      }

      const removeBtn = e.target.closest('#remove-discount');
      if (removeBtn && this.contains(removeBtn)) {
        e.preventDefault();
        this.removeDiscount().catch(console.error);
      }
    };

    this.addEventListener('click', this._onHostClick);
  }

  _initAccordionA11yState() {
    const details = this.querySelector('#Details-Cart-Discount') ?? this.querySelector('details');
    if (!details) return;

    const summary = details.querySelector('summary');
    if (!summary) return;

    this._discountDetails = details;

    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', String(details.open));

    this._detailsToggleHandler = () => {
      summary.setAttribute('aria-expanded', String(details.open));
    };

    details.addEventListener('toggle', this._detailsToggleHandler);
  }

  async _initDiscountState() {
    try {
      const cart = await this._fetchCart();
      if (cart.item_count === 0) {
        await this.clearAppliedDiscount();
        return;
      }
      const hasApplicable = this._parseDiscounts(cart).some((d) => d.applicable);
      hasApplicable ? this.showSuccessMessage() : this.hideSuccessMessage();
    } catch {
      // Silently ignore load-time failures
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  async applyDiscount(code) {
    const applyBtn = this.querySelector('#apply-discount');
    try {
      this.showButtonLoading(applyBtn);

      await fetch(`/discount/${encodeURIComponent(code)}?v=${Date.now()}&redirect=/cart`, {
        credentials: 'same-origin',
      });

      const cart = await this._fetchCart();
      const normalized = code.toLowerCase();
      const match = this._parseDiscounts(cart).find(
        ({ title, code: c }) =>
          title.toLowerCase() === normalized || c.toLowerCase() === normalized
      );
      const applied = Boolean(match?.applicable);

      // A real code whose conditions this cart doesn't meet (minimum spend,
      // product/customer restrictions, …) stays attached with applicable:false.
      // Detach it so it can't resurface as "applied" after a re-render or at checkout.
      if (match && !applied) await this.clearAppliedDiscount();

      const newDrawer = await this._fetchUpdatedCart();
      const target = newDrawer?.querySelector('cart-discount-code') ?? this;

      this._keepDiscountAccordionOpen(target);

      if (applied) {
        target.setDiscountMessage('');
        target.showSuccessMessage();
        const input = target.querySelector('#discount-code');
        if (input) input.value = '';
      } else {
        const message = match
          ? target.dataset.errorNotApplicable || "Please enter a valid discount code"
          : target.dataset.errorInvalid || 'Please enter a valid discount code';
        target.setDiscountMessage(message);
        target.hideSuccessMessage();
      }
    } catch (err) {
      console.error('Error applying discount:', err);
    } finally {
      this.hideButtonLoading(applyBtn);
    }
  }

  async removeDiscount() {
    const removeBtn = this.querySelector('#remove-discount');
    try {
      this.showButtonLoading(removeBtn);
      await this.clearAppliedDiscount();
      await this._fetchUpdatedCart();
    } catch (err) {
      console.error('Error removing discount:', err);
    } finally {
      this.hideButtonLoading(removeBtn);
    }
  }

  async clearAppliedDiscount() {
    this.hideSuccessMessage();
    this.setDiscountMessage('');

    const input = this.querySelector('#discount-code');
    if (input) input.value = '';

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
        credentials: 'same-origin',
      });
    } catch {
      // Ignore server-side clearing errors
    }

    await this._refreshCartIcon();
    this._clearDiscountCookie();
  }

  // ─── UI State ─────────────────────────────────────────────────────────────

  setDiscountMessage(text) {
    const message = this.querySelector('#discount-message');
    if (!message) return;

    const hasError = Boolean(text);
    message.textContent = text || '';
    message.hidden = !hasError;

    this.querySelector('#discount-code')?.classList.toggle('cart__discount-input--error', hasError);
    this.querySelector('.cart__discount')?.classList.toggle('cart__discount--error', hasError);
  }

  showSuccessMessage() {
    this.querySelector('#discount-code')?.style.setProperty('display', 'none');
    this.querySelector('#apply-discount')?.style.setProperty('display', 'none');
    const success = this.querySelector('#applied-discount');
    if (success) success.hidden = false;
  }

  hideSuccessMessage() {
    this.querySelector('#discount-code')?.style.removeProperty('display');
    this.querySelector('#apply-discount')?.style.removeProperty('display');
    const success = this.querySelector('#applied-discount');
    if (success) success.hidden = true;
  }

  // ─── Button Loading ───────────────────────────────────────────────────────

  showButtonLoading(button) {
    if (!button) return;
    this._ensureButtonSpinner(button);
    button.classList.add('loading');
    button.disabled = true;
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
  }

  hideButtonLoading(button) {
    if (!button) return;
    button.classList.remove('loading');
    button.disabled = false;
    button.querySelector('.loading__spinner')?.classList.add('hidden');
  }

  _ensureButtonSpinner(button) {
    if (button.querySelector('.loading__spinner')) return;
    const spinner = document.createElement('span');
    spinner.className = 'loading__spinner hidden';
    spinner.setAttribute('aria-hidden', 'true');
    spinner.innerHTML = `
      <svg class="spinner" viewBox="0 0 50 50" width="20" height="20" aria-hidden="true">
        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
      </svg>`;
    button.appendChild(spinner);
  }

  // ─── Cart Fetching ────────────────────────────────────────────────────────

  async _fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    return res.json();
  }

  async _fetchUpdatedCart() {
    try {
      // The cart-drawer snippet renders empty when requested against the cart template
      // (see cart-drawer.js refreshContents), so fetch against the store root instead.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const sections = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
        cache: 'no-store',
      }).then((r) => r.json());

      const parser = new DOMParser();
      const oldDrawer = document.querySelector('cart-drawer');
      const wasActive = oldDrawer?.classList.contains('active') ?? false;

      const container = document.createElement('div');
      container.innerHTML = sections['cart-drawer'];
      const newDrawer = container.querySelector('cart-drawer');

      if (oldDrawer && newDrawer) {
        oldDrawer.replaceWith(newDrawer);
        customElements.upgrade(newDrawer);
        if (wasActive) newDrawer.classList.add('active', 'animate');
      }

      if (sections['cart-icon-bubble']) {
        const iconDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newIcon = iconDoc.getElementById('cart-icon-bubble');
        if (newIcon) document.getElementById('cart-icon-bubble')?.replaceWith(newIcon);
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));

      return newDrawer ?? null;
    } catch (err) {
      console.error('Error updating cart:', err);
      return null;
    }
  }

  async _refreshCartIcon() {
    try {
      const text = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`, { cache: 'no-store' }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newBubble = doc.getElementById('cart-icon-bubble');
      if (newBubble) document.getElementById('cart-icon-bubble')?.replaceWith(newBubble);
    } catch {
      // ignore
    }
  }

  // ─── Discount Parsing ─────────────────────────────────────────────────────

  _parseDiscounts(cartData) {
    const sources = [
      cartData.cart_level_discount_applications,
      cartData.discount_applications,
      cartData.discount_codes,
    ].filter(Array.isArray);

    const source = sources.flat();
    if (source.length === 0) return [];

    // Entries from cart.discount_codes carry an `applicable` flag: false means the
    // code exists but this cart doesn't meet its conditions (minimum spend,
    // product/customer restrictions, …). Entries from the *_discount_applications
    // arrays have no such flag — they only exist when actually applied, so they
    // count as applicable. Callers gate success on this flag.
    return source.map((item) => {
      if (typeof item === 'string') {
        return { title: item, code: item, type: 'discount_code', applicable: true };
      }
      const code = item.code || item.title || '';
      return {
        title: item.title || code,
        code,
        type: item.type ?? (code ? 'discount_code' : 'automatic'),
        applicable: item.applicable !== false,
      };
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  _keepDiscountAccordionOpen(target) {
    try {
      const details =
        target.querySelector('#Details-Cart-Discount') ?? target.querySelector('details');
      if (details) {
        details.open = true;
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'true');
      }
    } catch {
      // Ignore
    }
  }

  _clearDiscountCookie() {
    const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const paths = ['/', '/cart', '/checkout'];
    const names = ['discount_code', 'discount', 'cart_discount'];
    const { hostname } = location;

    paths.forEach((path) => {
      names.forEach((name) => {
        const base = `${name}=; path=${path}`;
        document.cookie = `${base}; expires=${expired}`;
        document.cookie = `${base}; Max-Age=0`;
        document.cookie = `${base}; expires=${expired}; domain=${hostname}`;
        document.cookie = `${base}; Max-Age=0; domain=${hostname}`;
      });
    });

    try {
      localStorage.removeItem('discountCode');
      sessionStorage.removeItem('discountCode');
    } catch {
      // Ignore storage errors
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ─── Cart Empty Observer ──────────────────────────────────────────────────

  _observeCartEmpty() {
    // Guard: only one observer per page, not per instance
    if (CartDiscountCode._cartEmptyObserverAttached) return;
    CartDiscountCode._cartEmptyObserverAttached = true;

    const init = () => {
      // Watch document.body so the observer survives cart-drawer DOM replacement
      new MutationObserver(() => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        const isEmpty = cartDrawer.classList.contains('is-empty');

        // Only act on transition TO empty
        if (isEmpty && !cartDrawer._wasEmpty) {
          const discountEl = cartDrawer.querySelector('cart-discount-code');
          if (discountEl) {
            discountEl.clearAppliedDiscount().catch(console.error);
          } else {
            // Fallback: clear via API directly if element not found
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: '' }),
              credentials: 'same-origin',
            })
              .then(() =>
                fetch(`${routes.cart_url}?section_id=cart-icon-bubble`, { cache: 'no-store' })
                  .then((r) => r.text())
                  .then((text) => {
                    const doc = new DOMParser().parseFromString(text, 'text/html');
                    const newBubble = doc.getElementById('cart-icon-bubble');
                    if (newBubble) document.getElementById('cart-icon-bubble')?.replaceWith(newBubble);
                  })
              )
              .catch(console.error);
          }
        }

        cartDrawer._wasEmpty = isEmpty;
      }).observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    };

    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', init)
      : init();
  }
}

customElements.define('cart-discount-code', CartDiscountCode);
